(function () {
  'use strict';

  const DEFAULT_API_ENDPOINT = 'https://api-dev.tennesseeknifecompany.com/graphql';
  const REGISTER_FOR_DROP_MUTATION = 'mutation RegisterForDrop($dropId: ID!, $email: String!) { registerForDrop(drop_id: $dropId, email: $email) { id email notified registered_at drop_id user_id } }';

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  function extractToken(value) {
    if (!value) return '';

    const rawValue = String(value).trim();
    if (!rawValue) return '';

    try {
      const parsed = JSON.parse(rawValue);
      if (parsed && typeof parsed === 'object') {
        return extractToken(parsed.accessToken || parsed.token || parsed.authToken || parsed.jwt);
      }
    } catch (error) {
      // Stored token is already a plain string.
    }

    return rawValue.replace(/^Bearer\s+/i, '').trim();
  }

  function getAuthConfig() {
    return window.TKC && window.TKC.authConfig ? window.TKC.authConfig : {};
  }

  function getToken() {
    const config = getAuthConfig();
    const keys = [
      config.tokenKey,
      ...(Array.isArray(config.tokenFallbackKeys) ? config.tokenFallbackKeys : []),
      'tkcAccessToken',
      'tkcAssesstoken',
      'accessToken',
      'token',
      'authToken'
    ].filter((key, index, list) => key && list.indexOf(key) === index);

    for (let index = 0; index < keys.length; index += 1) {
      const token = extractToken(window.localStorage.getItem(keys[index]));
      if (token) return token;
    }

    return '';
  }

  function getStoredCustomerEmail() {
    const config = getAuthConfig();
    const keys = [
      config.userKey,
      'tkcUser',
      'customer',
      'user'
    ].filter((key, index, list) => key && list.indexOf(key) === index);

    for (let index = 0; index < keys.length; index += 1) {
      const raw = window.localStorage.getItem(keys[index]);
      if (!raw) continue;

      try {
        const parsed = JSON.parse(raw);
        const email = firstDefined(
          parsed && parsed.email,
          parsed && parsed.customerEmail,
          parsed && parsed.customer_email,
          parsed && parsed.user && parsed.user.email,
          parsed && parsed.customer && parsed.customer.email
        );
        if (email) return String(email).trim();
      } catch (error) {
        // Ignore malformed stored user data.
      }
    }

    return '';
  }

  function getGraphqlErrorMessage(error) {
    if (!error) return '';
    const extensions = error.extensions || {};
    const nested = extensions.originalError || extensions.exception || extensions.response || {};
    const detail = nested.message || extensions.message;

    if (Array.isArray(detail)) return detail.filter(Boolean).join(' ');
    if (detail) return String(detail);
    return error.message || '';
  }

  async function request(endpoint, query, variables, options) {
    const opts = options || {};
    const token = getToken();
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'apollo-require-preflight': 'true',
      'ngrok-skip-browser-warning': 'true'
    };

    if (opts.auth === true) {
      if (!token) throw new Error('Please log in to get notified.');
      headers.Authorization = `Bearer ${token}`;
    } else if (opts.auth === 'optional' && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query, variables: variables || {} })
    });
    const payload = await response.json().catch(() => null);

    if (payload && payload.errors && payload.errors.length) {
      const message = payload.errors.map(getGraphqlErrorMessage).filter(Boolean).join(' ');
      throw new Error(message || 'Request failed. Please try again.');
    }

    if (!response.ok) {
      throw new Error((payload && payload.message) || 'Request failed. Please try again.');
    }

    return payload ? payload.data : null;
  }

  function firstDefined() {
    for (let index = 0; index < arguments.length; index += 1) {
      if (arguments[index] !== null && arguments[index] !== undefined && arguments[index] !== '') {
        return arguments[index];
      }
    }
    return '';
  }

  function normalizeStatus(status) {
    return String(status || '').toUpperCase();
  }

  function isLiveDrop(drop) {
    return ['LIVE', 'LAUNCHED', 'ACTIVE'].indexOf(normalizeStatus(drop && drop.status)) !== -1;
  }

  function isScheduledDrop(drop) {
    return normalizeStatus(drop && drop.status) === 'SCHEDULED';
  }

  function getNestedCollectionValue(drop, key) {
    const collection = firstDefined(
      drop && drop.collection,
      drop && drop.shopifyCollection,
      drop && drop.shopify_collection
    );

    return collection && typeof collection === 'object' ? collection[key] : '';
  }

  function getDropTitle(drop) {
    return firstDefined(
      drop && drop.collectionTitle,
      drop && drop.collection_title,
      drop && drop.collectionName,
      drop && drop.collection_name,
      drop && drop.shopifyCollectionTitle,
      drop && drop.shopify_collection_title,
      getNestedCollectionValue(drop, 'title'),
      getNestedCollectionValue(drop, 'name'),
      drop && drop.name,
      drop && drop.title,
      drop && drop.dropTitle,
      drop && drop.drop_title,
      'Scheduled drop'
    );
  }

  function getDropHandle(drop) {
    return firstDefined(
      drop && drop.collectionHandle,
      drop && drop.collection_handle,
      drop && drop.shopifyCollectionHandle,
      drop && drop.shopify_collection_handle,
      getNestedCollectionValue(drop, 'handle'),
      drop && drop.handle
    );
  }

  function getDropLaunchAt(drop) {
    return firstDefined(drop && drop.launchAt, drop && drop.launch_at, drop && drop.launch_at_utc);
  }

  function getDropCreatedAt(drop) {
    return firstDefined(drop && drop.createdAt, drop && drop.created_at);
  }

  function getDropTeaser(drop) {
    return firstDefined(drop && drop.teaserDescription, drop && drop.teaser_description);
  }

  function normalizeCollectionUrl(value) {
    const url = String(value || '').trim();
    if (!url) return '';
    if (/^https?:\/\//i.test(url) || url.charAt(0) === '/') return url;
    return `/collections/${encodeURIComponent(url.replace(/^collections\//, '').replace(/^\/+/, ''))}`;
  }

  function getCollectionUrl(drop) {
    const url = firstDefined(
      drop && drop.collectionUrl,
      drop && drop.collection_url,
      drop && drop.shopifyCollectionUrl,
      drop && drop.shopify_collection_url,
      getNestedCollectionValue(drop, 'url')
    );

    if (url) return normalizeCollectionUrl(url);

    const handle = String(getDropHandle(drop)).replace(/^\/+/, '');
    return handle ? `/collections/${encodeURIComponent(handle)}` : '';
  }

  function selectDrop(drops) {
    const now = Date.now();
    const entries = (drops || []).filter(Boolean);
    const live = entries
      .filter(isLiveDrop)
      .sort((a, b) => new Date(getDropCreatedAt(b) || 0).getTime() - new Date(getDropCreatedAt(a) || 0).getTime());

    if (live.length) return live[0];

    const scheduled = entries
      .filter(isScheduledDrop)
      .filter((drop) => !Number.isNaN(new Date(getDropLaunchAt(drop)).getTime()));
    const future = scheduled
      .filter((drop) => new Date(getDropLaunchAt(drop)).getTime() > now)
      .sort((a, b) => new Date(getDropLaunchAt(a)).getTime() - new Date(getDropLaunchAt(b)).getTime());

    if (future.length) return future[0];

    return scheduled
      .sort((a, b) => new Date(getDropCreatedAt(b) || 0).getTime() - new Date(getDropCreatedAt(a) || 0).getTime())[0] || null;
  }

  function buildCountdownMarkup() {
    return `
      <div class="countdown__grid inline-grid no-js-hidden">
        <div class="countdown__item text-center">
          <strong class="countdown__number inline-block relative js-days">0</strong>
          <div class="countdown__unit text-sm">Days</div>
        </div>
        <div class="countdown__item text-center">
          <strong class="countdown__number inline-block relative js-hours">0</strong>
          <div class="countdown__unit text-sm">Hours</div>
        </div>
        <div class="countdown__item text-center">
          <strong class="countdown__number inline-block relative js-mins">0</strong>
          <div class="countdown__unit text-sm">Minutes</div>
        </div>
        <div class="countdown__item text-center">
          <strong class="countdown__number inline-block relative js-secs">0</strong>
          <div class="countdown__unit text-sm">Seconds</div>
        </div>
      </div>
    `;
  }

  function parseDropPayload(value) {
    if (!value) return null;

    let parsed = value;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      if (typeof parsed !== 'string') break;

      try {
        parsed = JSON.parse(parsed);
      } catch (error) {
        return null;
      }
    }

    return parsed && typeof parsed === 'object' ? parsed : null;
  }

  class DropCountdown {
    constructor(root) {
      this.root = root;
      this.endpoint = root.dataset.dropEndpoint || getAuthConfig().endpoint || DEFAULT_API_ENDPOINT;
      this.loginUrl = root.dataset.dropLoginUrl || getAuthConfig().loginUrl || '/pages/login';
      this.currentDrop = null;
      this.interval = null;
      this.eyebrow = root.querySelector('[data-drop-countdown-eyebrow]');
      this.title = root.querySelector('[data-drop-countdown-title]');
      this.titleLink = root.querySelector('[data-drop-countdown-title-link]');
      this.timer = root.querySelector('[data-drop-countdown-timer]');
      this.seeButton = root.querySelector('[data-drop-countdown-see]');
      this.notifyButton = root.querySelector('[data-drop-countdown-notify]');
      this.message = root.querySelector('[data-drop-countdown-message]');
      this.initialDrops = this.readInitialDrops();
      this.notifyModal = null;
      this.notifyEmailInput = null;
      this.notifyModalMessage = null;
      this.notifySubmitButton = null;
      this.lastFocusedElement = null;
      this.modalId = `tkc-drop-notify-${String(root.id || Math.random().toString(36).slice(2)).replace(/[^a-zA-Z0-9_-]/g, '')}`;

      this.bind();
      this.setNotifyEnabled(false);
      this.load();
    }

    bind() {
      if (!this.notifyButton) return;
      this.notifyButton.addEventListener('click', (event) => this.register(event));
    }

    readInitialDrops() {
      return Array.from(this.root.querySelectorAll('[data-drop-countdown-initial-drop]')).map((dataEl) => {
        try {
          const drop = parseDropPayload(dataEl.textContent || 'null');
          if (!drop) return null;

          if (dataEl.dataset.dropCountdownSource === 'current') {
            return Object.assign({}, drop, { status: 'LIVE' });
          }

          if (dataEl.dataset.dropCountdownSource === 'next') {
            return Object.assign({}, drop, { status: 'SCHEDULED' });
          }

          return drop;
        } catch (error) {
          return null;
        }
      }).filter(Boolean);
    }

    load() {
      const initialDrop = selectDrop(this.initialDrops);
      if (initialDrop) {
        this.applyDrop(initialDrop);
        return;
      }

      this.hideSection();
    }

    applyDrop(drop) {
      const title = getDropTitle(drop);
      const collectionUrl = getCollectionUrl(drop);
      const teaser = getDropTeaser(drop);

      this.currentDrop = drop;
      this.root.hidden = false;
      this.root.removeAttribute('aria-hidden');

      if (this.title) this.title.textContent = title;
      this.configureEyebrow(drop);
      if (this.titleLink) {
        if (collectionUrl) {
          this.titleLink.href = collectionUrl;
          this.titleLink.removeAttribute('aria-disabled');
        } else {
          this.titleLink.removeAttribute('href');
          this.titleLink.removeAttribute('aria-disabled');
        }
      }

      if (isLiveDrop(drop)) {
        this.renderLiveDrop(teaser);
      } else {
        this.renderCountdown(getDropLaunchAt(drop), teaser);
      }
      this.configureCta(drop, collectionUrl);
    }

    configureEyebrow(drop) {
      if (!this.eyebrow) return;

      const scheduledLabel = this.eyebrow.dataset.scheduledLabel || this.eyebrow.dataset.defaultLabel || 'GET NOTIFICATIONS FOR OUR';
      const liveLabel = this.eyebrow.dataset.liveLabel || 'THE DROP IS LIVE - CLAIM YOUR BLADE';
      this.eyebrow.textContent = isLiveDrop(drop) ? liveLabel : scheduledLabel;
    }

    hideSection() {
      window.clearInterval(this.interval);
      this.currentDrop = null;
      this.setSeeButton('', false);
      this.setNotifyEnabled(false);
      this.root.hidden = true;
      this.root.setAttribute('aria-hidden', 'true');
    }

    renderLiveDrop(teaser) {
      if (!this.timer) return;

      window.clearInterval(this.interval);
      this.timer.innerHTML = `
        <div class="countdown tkc-drop-countdown__live" role="status" aria-live="polite">
          <div class="countdown__text rte">
            <strong>Drop is live now.</strong>
            ${teaser ? `<br>${this.escape(teaser).replace(/\n/g, '<br>')}` : ''}
          </div>
        </div>
      `;
    }

    renderCountdown(launchAt, teaser) {
      if (!this.timer) return;

      const endTime = new Date(launchAt).getTime();
      if (Number.isNaN(endTime)) return;

      window.clearInterval(this.interval);
      this.timer.innerHTML = `
        <div class="countdown" role="timer" aria-live="polite">
          ${buildCountdownMarkup()}
          ${teaser ? `<div class="countdown__text rte">${this.escape(teaser).replace(/\n/g, '<br>')}</div>` : ''}
        </div>
      `;

      const daysEl = this.timer.querySelector('.js-days');
      const hoursEl = this.timer.querySelector('.js-hours');
      const minsEl = this.timer.querySelector('.js-mins');
      const secsEl = this.timer.querySelector('.js-secs');
      const update = () => {
        const diff = Math.max(0, endTime - Date.now());
        const day = 24 * 60 * 60 * 1000;
        const hour = 60 * 60 * 1000;
        const minute = 60 * 1000;
        const days = Math.floor(diff / day);
        const hours = Math.floor((diff % day) / hour);
        const mins = Math.floor((diff % hour) / minute);
        const secs = Math.floor((diff % minute) / 1000);

        if (daysEl) daysEl.textContent = days;
        if (hoursEl) hoursEl.textContent = hours;
        if (minsEl) minsEl.textContent = mins;
        if (secsEl) secsEl.textContent = secs;

        if (diff <= 0) window.clearInterval(this.interval);
      };

      update();
      this.interval = window.setInterval(update, 1000);
    }

    register(event) {
      event.preventDefault();

      if (this.currentDrop && isLiveDrop(this.currentDrop)) {
        const collectionUrl = getCollectionUrl(this.currentDrop);
        if (collectionUrl) window.location.href = collectionUrl;
        return;
      }

      if (this.notifyButton && this.notifyButton.getAttribute('aria-disabled') === 'true') {
        return;
      }

      if (!this.currentDrop || !this.currentDrop.id) {
        this.setMessage('No scheduled drop is available yet.', 'error');
        return;
      }

      this.openNotifyModal();
    }

    ensureNotifyModal() {
      if (this.notifyModal) return this.notifyModal;

      DropCountdown.injectNotifyModalStyles();

      const modal = document.createElement('div');
      const titleId = `${this.modalId}-title`;
      const emailId = `${this.modalId}-email`;
      modal.className = 'tkc-drop-notify-modal';
      modal.hidden = true;
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', titleId);
      modal.innerHTML = `
        <button class="tkc-drop-notify-modal__backdrop" type="button" data-drop-notify-close aria-label="Close notification form"></button>
        <div class="tkc-drop-notify-modal__panel">
          <button class="tkc-drop-notify-modal__close" type="button" data-drop-notify-close aria-label="Close notification form">&times;</button>
          <form class="tkc-drop-notify-modal__form" data-drop-notify-form>
            <h2 class="tkc-drop-notify-modal__title" id="${titleId}">Get Drop Notification</h2>
            <p class="tkc-drop-notify-modal__copy" data-drop-notify-copy></p>
            <label class="tkc-drop-notify-modal__label" for="${emailId}">Email address</label>
            <input class="tkc-drop-notify-modal__input" id="${emailId}" name="email" type="email" autocomplete="email" required>
            <div class="tkc-drop-notify-modal__message" data-drop-notify-message role="status" aria-live="polite" hidden></div>
            <div class="tkc-drop-notify-modal__actions">
              <button class="btn btn--primary" type="submit" data-drop-notify-submit>Never Miss a Drop</button>
              <button class="btn btn--secondary" type="button" data-drop-notify-close>Cancel</button>
            </div>
          </form>
        </div>
      `;

      document.body.appendChild(modal);

      this.notifyModal = modal;
      this.notifyEmailInput = modal.querySelector('input[name="email"]');
      this.notifyModalMessage = modal.querySelector('[data-drop-notify-message]');
      this.notifySubmitButton = modal.querySelector('[data-drop-notify-submit]');

      modal.addEventListener('click', (event) => {
        if (event.target.closest('[data-drop-notify-close]')) this.closeNotifyModal();
      });

      modal.querySelector('[data-drop-notify-form]').addEventListener('submit', (event) => this.submitNotifyForm(event));

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.notifyModal && !this.notifyModal.hidden) {
          this.closeNotifyModal();
        }
      });

      return modal;
    }

    openNotifyModal() {
      const modal = this.ensureNotifyModal();
      const copy = modal.querySelector('[data-drop-notify-copy]');
      const title = getDropTitle(this.currentDrop);
      const email = firstDefined(this.root.dataset.dropCustomerEmail, getStoredCustomerEmail());

      if (copy) copy.textContent = title ? `Enter your email for ${title}.` : 'Enter your email for this drop.';
      if (this.notifyEmailInput) {
        this.notifyEmailInput.value = email;
        this.notifyEmailInput.setCustomValidity('');
      }
      this.setNotifyModalMessage('', '');
      this.setNotifyFormPending(false);

      this.lastFocusedElement = document.activeElement;
      modal.hidden = false;
      document.documentElement.classList.add('tkc-drop-notify-modal-open');

      window.requestAnimationFrame(() => {
        if (this.notifyEmailInput) this.notifyEmailInput.focus();
      });
    }

    closeNotifyModal() {
      if (!this.notifyModal) return;

      this.notifyModal.hidden = true;
      document.documentElement.classList.remove('tkc-drop-notify-modal-open');

      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus();
      }
    }

    async submitNotifyForm(event) {
      event.preventDefault();

      if (!this.currentDrop || !this.currentDrop.id) {
        this.setNotifyModalMessage('No scheduled drop is available yet.', 'error');
        return;
      }

      const email = this.notifyEmailInput ? this.notifyEmailInput.value.trim() : '';
      if (!email) {
        this.setNotifyModalMessage('Enter an email address.', 'error');
        return;
      }

      if (this.notifyEmailInput && !this.notifyEmailInput.checkValidity()) {
        this.notifyEmailInput.reportValidity();
        return;
      }

      try {
        this.setNotifyModalMessage('', '');
        this.setNotifyFormPending(true);
        await request(this.endpoint, REGISTER_FOR_DROP_MUTATION, { dropId: this.currentDrop.id, email }, { auth: 'optional' });
        this.setMessage("You're registered for this drop.", 'success');
        this.setRegistered();
        this.closeNotifyModal();
      } catch (error) {
        this.setNotifyFormPending(false);
        this.setNotifyModalMessage(error.message || 'Drop registration failed. Please try again.', 'error');
      }
    }

    setNotifyFormPending(pending) {
      if (!this.notifySubmitButton) return;

      this.notifySubmitButton.disabled = pending;
      this.notifySubmitButton.textContent = pending ? 'Registering...' : 'Never Miss a Drop';
    }

    setNotifyModalMessage(message, type) {
      if (!this.notifyModalMessage) return;

      this.notifyModalMessage.hidden = !message;
      this.notifyModalMessage.textContent = message || '';
      this.notifyModalMessage.className = type
        ? `tkc-drop-notify-modal__message is-${type}`
        : 'tkc-drop-notify-modal__message';
    }

    setNotifyEnabled(enabled) {
      if (!this.notifyButton) return;
      this.notifyButton.classList.toggle('is-disabled', !enabled);
      this.notifyButton.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    }

    configureCta(drop, collectionUrl) {
      if (!this.notifyButton && !this.seeButton) return;

      this.setSeeButton(collectionUrl, Boolean(collectionUrl));

      if (isLiveDrop(drop)) {
        if (this.notifyButton && this.seeButton) {
          this.notifyButton.hidden = true;
          this.setNotifyEnabled(false);
        } else if (this.notifyButton) {
          this.notifyButton.hidden = false;
          this.notifyButton.textContent = 'See Drop';
          this.notifyButton.href = collectionUrl || '#';
          this.notifyButton.classList.toggle('is-disabled', !collectionUrl);
          if (collectionUrl) {
            this.notifyButton.removeAttribute('aria-disabled');
          } else {
            this.notifyButton.setAttribute('aria-disabled', 'true');
          }
        }
        return;
      }

      if (this.notifyButton) {
        this.notifyButton.hidden = false;
        this.notifyButton.textContent = this.notifyButton.dataset.defaultLabel || 'Never Miss a Drop';
        this.notifyButton.href = '#';
        this.setNotifyEnabled(isScheduledDrop(drop));
      }
    }

    setSeeButton(collectionUrl, enabled) {
      if (!this.seeButton) return;

      this.seeButton.hidden = false;
      this.seeButton.textContent = 'See Drop';
      this.seeButton.href = collectionUrl || '#';
      this.seeButton.classList.toggle('is-disabled', !enabled);
      if (enabled) {
        this.seeButton.removeAttribute('aria-disabled');
      } else {
        this.seeButton.setAttribute('aria-disabled', 'true');
      }
    }

    setButtonPending(pending) {
      if (!this.notifyButton) return;
      const defaultLabel = this.notifyButton.dataset.defaultLabel || this.notifyButton.textContent;
      this.notifyButton.textContent = pending ? 'Registering...' : defaultLabel;
      this.notifyButton.classList.toggle('is-disabled', pending);
      this.notifyButton.setAttribute('aria-disabled', pending ? 'true' : 'false');
    }

    setRegistered() {
      if (!this.notifyButton) return;
      this.notifyButton.textContent = 'Registered';
      this.notifyButton.classList.add('is-disabled');
      this.notifyButton.setAttribute('aria-disabled', 'true');
    }

    setMessage(message, type) {
      if (!this.message) return;
      this.message.hidden = !message;
      this.message.textContent = message || '';
      this.message.className = type ? `tkc-drop-countdown__message text-overlay__block is-${type}` : 'tkc-drop-countdown__message text-overlay__block';
    }

    static injectNotifyModalStyles() {
      if (document.getElementById('tkc-drop-notify-modal-styles')) return;

      const style = document.createElement('style');
      style.id = 'tkc-drop-notify-modal-styles';
      style.textContent = `
        .tkc-drop-notify-modal[hidden] {
          display: none;
        }

        .tkc-drop-notify-modal {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .tkc-drop-notify-modal-open {
          overflow: hidden;
        }

        .tkc-drop-notify-modal__backdrop {
          position: absolute;
          inset: 0;
          border: 0;
          background: rgba(0, 0, 0, 0.58);
          cursor: pointer;
        }

        .tkc-drop-notify-modal__panel {
          position: relative;
          width: min(100%, 460px);
          border: 1px solid rgba(255, 255, 255, 0.16);
          border-radius: 8px;
          background: #18130f;
          color: #fff;
          box-shadow: 0 24px 70px rgba(0, 0, 0, 0.34);
        }

        .tkc-drop-notify-modal__form {
          display: grid;
          gap: 14px;
          padding: 28px;
        }

        .tkc-drop-notify-modal__title {
          margin: 0;
          color: #f2d28b;
          font-size: 24px;
          line-height: 1.2;
        }

        .tkc-drop-notify-modal__copy {
          margin: 0;
          color: rgba(255, 255, 255, 0.76);
          line-height: 1.5;
        }

        .tkc-drop-notify-modal__label {
          color: #f2d28b;
          font-weight: 700;
        }

        .tkc-drop-notify-modal__input {
          width: 100%;
          min-height: 48px;
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 6px;
          background: #0f0d0a;
          color: #fff;
          padding: 11px 14px;
          font: inherit;
        }

        .tkc-drop-notify-modal__input:focus {
          outline: 0;
          border-color: #f2d28b;
          box-shadow: 0 0 0 3px rgba(242, 210, 139, 0.18);
        }

        .tkc-drop-notify-modal__message {
          border-radius: 6px;
          padding: 10px 12px;
          background: rgba(255, 255, 255, 0.08);
          color: rgba(255, 255, 255, 0.84);
          font-size: 14px;
        }

        .tkc-drop-notify-modal__message[hidden] {
          display: none;
        }

        .tkc-drop-notify-modal__message.is-error {
          background: rgba(199, 70, 18, 0.14);
          color: #ffd8d4;
        }

        .tkc-drop-notify-modal__actions {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          align-items: center;
        }

        .tkc-drop-notify-modal__close {
          position: absolute;
          top: 12px;
          right: 12px;
          width: 36px;
          height: 36px;
          border: 1px solid rgba(255, 255, 255, 0.14);
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
          cursor: pointer;
          font-size: 24px;
          line-height: 1;
        }

        .tkc-drop-notify-modal__close:hover,
        .tkc-drop-notify-modal__close:focus-visible {
          border-color: #f2d28b;
          color: #f2d28b;
        }

        @media (max-width: 520px) {
          .tkc-drop-notify-modal {
            padding: 14px;
          }

          .tkc-drop-notify-modal__form {
            padding: 24px 18px;
          }

          .tkc-drop-notify-modal__actions .btn {
            width: 100%;
          }
        }
      `;

      document.head.appendChild(style);
    }

    escape(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  onReady(() => {
    document.querySelectorAll('[data-drop-countdown-section]').forEach((root) => {
      if (root.dataset.dropCountdownBound === 'true') return;
      root.dataset.dropCountdownBound = 'true';
      new DropCountdown(root);
    });
  });
})();
